import React, { useState } from 'react';
import { ArrowRight, ArrowLeft, X, Check, Sparkles, Camera, Share2, Gift, Palette } from 'lucide-react';
import { trackEvent } from '../services/analytics';

const ONBOARDING_VERSION = 3;
const STORAGE_KEY = `stagery_onboarded_v${ONBOARDING_VERSION}`;

/**
 * STA-168 — In-memory fallback for the "onboarding dismissed" flag.
 *
 * `localStorage` can throw or be unavailable (Safari private mode, storage
 * disabled, quota exceeded). When that happens the persistent write below is
 * a no-op, so this module-level flag keeps the modal dismissed for the rest
 * of the session: it survives SPA navigations because the module instance
 * persists. Previously an unguarded `localStorage` call could throw inside
 * `handleSkip`, abort before `onDone()`, and leave the modal stuck so it
 * reappeared on every page navigation after the user clicked OVERSLAAN.
 */
let dismissedThisSession = false;

export function hasSeenOnboarding(): boolean {
  if (dismissedThisSession) return true;
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    // localStorage unavailable — rely on the in-memory flag only.
    return false;
  }
}

export function markOnboardingDone() {
  // Set the in-memory flag first so dismissal sticks for the session even if
  // the persistent write throws.
  dismissedThisSession = true;
  try {
    localStorage.setItem(STORAGE_KEY, 'true');
  } catch {
    // localStorage unavailable (private mode / disabled / quota). The
    // in-memory flag above still keeps the modal dismissed this session.
  }
}

export function resetOnboarding() {
  dismissedThisSession = false;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // localStorage unavailable — nothing to clear.
  }
}

interface Props {
  onDone: () => void;
  credits?: number;
  firstName?: string;
}

interface StepDef {
  title: string;
  kicker: string;
  icon: React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }>;
  render: (props: { credits: number; firstName?: string }) => React.ReactNode;
}

const STEPS: StepDef[] = [
  {
    kicker: 'Welkom',
    title: 'Fijn dat je er bent.',
    icon: Sparkles,
    render: ({ firstName }) => (
      <>
        <p className="text-sm text-ink/70 leading-relaxed mb-3">
          {firstName ? `Hoi ${firstName}! ` : ''}Stagery helpt je om leegstaande en
          gedateerde woningen Funda-klaar te maken. Upload een foto, kies uit 3 AI-varianten,
          download het resultaat.
        </p>
        <p className="text-sm text-ink/70 leading-relaxed">
          In 4 stappen laten we zien wat je allemaal kunt. Daarna ben je klaar om te beginnen.
        </p>
      </>
    ),
  },
  {
    kicker: 'Zo werkt het',
    title: 'Foto uploaden, 3 versies kiezen, klaar.',
    icon: Camera,
    render: () => (
      <>
        <div className="space-y-3">
          <div className="flex gap-3">
            <span className="w-7 h-7 bg-black text-white text-xs flex items-center justify-center shrink-0">1</span>
            <div>
              <div className="text-sm font-semibold mb-0.5">Upload een foto</div>
              <div className="text-xs text-ink/60">Per ruimte één heldere foto. AI ruimt op en richt in.</div>
            </div>
          </div>
          <div className="flex gap-3">
            <span className="w-7 h-7 bg-black text-white text-xs flex items-center justify-center shrink-0">2</span>
            <div>
              <div className="text-sm font-semibold mb-0.5">Kies uit 3 versies</div>
              <div className="text-xs text-ink/60">Je krijgt 3 varianten. Niet goed? Feedback geven, AI past aan.</div>
            </div>
          </div>
          <div className="flex gap-3">
            <span className="w-7 h-7 bg-accent text-ink text-xs flex items-center justify-center shrink-0">✓</span>
            <div>
              <div className="text-sm font-semibold mb-0.5">Download en deel</div>
              <div className="text-xs text-ink/60">Klaar voor Funda, brochures en social media.</div>
            </div>
          </div>
        </div>
      </>
    ),
  },
  {
    kicker: 'Presenteren',
    title: 'Elke woning krijgt een deelbare pagina.',
    icon: Share2,
    render: () => (
      <>
        <p className="text-sm text-ink/70 leading-relaxed mb-3">
          Na het restylen kun je een presentatiepagina openzetten voor je klant. Origineel en
          restyled naast elkaar, je kantoor-logo erboven, en een deel-knop voor WhatsApp.
        </p>
        <p className="text-sm text-ink/70 leading-relaxed">
          Je bepaalt zelf wanneer een pagina publiek is. Woning verkocht? Zet hem weer op privé.
        </p>
      </>
    ),
  },
  {
    kicker: 'Branding',
    title: 'Je eigen logo en stijl op alles.',
    icon: Palette,
    render: () => (
      <>
        <p className="text-sm text-ink/70 leading-relaxed mb-3">
          Ga naar je profiel en upload je kantoor-logo en profielfoto. Die verschijnen op je
          presentatiepagina's en in exports. Stel ook je exportlabel in (bijv. "Artist Impression")
          zodat dat automatisch op je downloads staat.
        </p>
        <p className="text-sm text-ink/70 leading-relaxed">
          Je kunt collega's uitnodigen om samen te werken. Iedereen onder hetzelfde kantoor.
        </p>
      </>
    ),
  },
  {
    kicker: 'Bonus',
    title: 'Voltooi alle stappen, ontvang 10 extra credits.',
    icon: Gift,
    render: () => (
      <>
        <p className="text-sm text-ink/70 leading-relaxed mb-3">
          We hebben een korte checklist samengesteld. Upload je logo, maak je eerste restyling,
          deel een presentatiepagina en nodig een collega uit. Zodra alles klaar is, krijg je
          10 extra credits als bonus.
        </p>
        <div className="border border-border/10 bg-accent/10 p-3">
          <p className="text-xs text-ink/70 leading-relaxed">
            Je vindt de checklist in je profiel. Gaat vanzelf - de meeste stappen doe je al
            bij normaal gebruik.
          </p>
        </div>
      </>
    ),
  },
];

export const OnboardingTooltip: React.FC<Props> = ({ onDone, credits = 25, firstName }) => {
  const [step, setStep] = useState(0);
  const current = STEPS[step];
  const Icon = current.icon;
  const isLast = step === STEPS.length - 1;

  const handleNext = () => {
    if (isLast) {
      markOnboardingDone();
      trackEvent('onboarding_completed', { stepsViewed: step + 1 });
      onDone();
    } else {
      setStep(step + 1);
    }
  };
  const handleBack = () => setStep(Math.max(0, step - 1));
  const handleSkip = () => {
    markOnboardingDone();
    trackEvent('onboarding_skipped', { atStep: step + 1 });
    onDone();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white border border-border shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] w-full max-w-md relative">
        <button
          onClick={handleSkip}
          className="absolute top-3 right-3 text-ink/30 hover:text-ink transition-colors z-10"
          aria-label="Sluit onboarding"
        >
          <X size={16} />
        </button>

        <div className="p-6 pb-5">
          <div className="flex gap-1 mb-5">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className={`h-1 flex-1 transition-colors ${i <= step ? 'bg-black' : 'bg-stone-200'}`}
              />
            ))}
          </div>

          <div className="flex items-center gap-2 mb-3">
            <Icon size={14} strokeWidth={2} className="text-ink/50" />
            <p className="text-[10px] text-ink/40 uppercase tracking-wide">
              {current.kicker} · Stap {step + 1}/{STEPS.length}
            </p>
          </div>

          <h3 className="font-sans text-2xl font-semibold leading-tight mb-4">{current.title}</h3>

          <div className="min-h-[140px]">{current.render({ credits, firstName })}</div>
        </div>

        <div className="flex items-center justify-between border-t border-border/10 px-6 py-4 bg-bg">
          <button
            onClick={step === 0 ? handleSkip : handleBack}
            className="text-[11px] font-bold uppercase tracking-wide text-ink/40 hover:text-ink transition-colors flex items-center gap-1"
          >
            {step === 0 ? (
              'Overslaan'
            ) : (
              <>
                <ArrowLeft size={12} /> Terug
              </>
            )}
          </button>
          <button
            onClick={handleNext}
            className="bg-ink text-white px-5 py-2.5 text-[11px] font-bold uppercase tracking-wide shadow-card hover:bg-accent hover:text-ink hover:  transition-all flex items-center gap-2"
          >
            {isLast ? 'Aan de slag' : 'Volgende'}
            <ArrowRight size={12} strokeWidth={2.5} />
          </button>
        </div>
      </div>
    </div>
  );
};
